import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  DeleteOutlined,
  Download,
  InsertDriveFileOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useDispatchWrapper } from "../../hooks";
import { showMessage } from "../../redux/messages/messagesActions";
import { downLoadFile, getFileNameFromUrl } from "./utils";
import {
  getFileExtension,
  getFileExtensionType,
  IFileExtension,
} from "../FileUploadIcon/helpers";
import { ViewImageDialog } from "../FileUploadIcon/ViewImageDialog";

interface ISingleFileDisplayV2Props {
  file?: File | null;
  value: string | null;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  moduleName?: string;
}

export const SingleFileDisplayV2: React.FC<ISingleFileDisplayV2Props> = (
  props
) => {
  const { file, value, onDelete, deleteDisabled, moduleName: _moduleName } = props;
  const dispatch = useDispatchWrapper();
  const [loading, setLoading] = React.useState(false);
  const [openFilePreview, setOpenFilePreview] = React.useState<string | null>(
    null
  );

  const handleDownloadFile = async () => {
    if (file) {
      const downloadLink = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = downloadLink;
      a.download = file.name || "download";
      a.click();
      URL.revokeObjectURL(downloadLink);
      return;
    }
    if (!loading) {
      try {
        setLoading(true);
        await downLoadFile(props.value);

        // For local files, we don't need to call API
        // dispatch(
        //   showMessage({
        //     displayAs: "snackbar",
        //     message: "File download not available for local files",
        //     type: "warning",
        //   })
        // );
      } catch (err) {
        dispatch(
          showMessage({
            displayAs: "snackbar",
            message: "Something went wrong!",
            type: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const [imageDialog, setImageDialog] = useState<{
    view: boolean;
    data: string;
    type: IFileExtension;
  }>({ view: false, data: "", type: "OTHER" });

  const handlePreviewFile = async () => {
    if (file) {
      const downloadLink = URL.createObjectURL(file);
      setOpenFilePreview(downloadLink);

      // For Excel files, we can show them in the preview dialog
      const fileType = getFileExtensionType(file.name);

      if (fileType === "EXCEL") {
        setImageDialog({ view: true, data: downloadLink, type: fileType });
      } else if (fileType === "CSV") {
        setImageDialog({ view: true, data: downloadLink, type: fileType });
      } else {
        window.open(downloadLink, "_blank");
      }
      return;
    } else {
      const fileUrl = props.value;

      if (!fileUrl) {
        dispatch(
          showMessage({
            type: "error",
            message: "No file available to view",
            displayAs: "snackbar",
          })
        );
        return;
      }

      const fileType = getFileExtensionType(fileUrl);

      if (fileType === "EXCEL") {
        setImageDialog({ view: true, data: fileUrl, type: fileType });
      } else if (fileType === "CSV") {
        setImageDialog({ view: true, data: fileUrl, type: fileType });
      } else {
        dispatch(
          showMessage({
            type: "warning",
            message: "Preview not available for this file type",
            displayAs: "snackbar",
          })
        );
      }
    }
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        borderRadius={2}
        position="relative"
        p={2}
        spacing={2}
        sx={{
          width: "100%",
          backgroundColor: "background.paper",
          boxShadow: 1,
          flexWrap: "wrap",

          border: "1px solid",
          borderColor: "divider",
          "&:hover": {
            boxShadow: 4,
            "& .file-actions": { opacity: 1 },
          },
          transition: "all 0.2s ease-in-out",
        }}
      >
        {/* File Type Icon */}
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1,
            backgroundColor: "primary.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <InsertDriveFileOutlined
            fontSize="medium"
            sx={{ color: "primary.main" }}
          />
        </Box>

        {/* File Name */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ fontWeight: 600, whiteSpace: "unset" }}
            title={file ? file.name : value ? getFileNameFromUrl(value) : ""}
          >
            {file ? file.name : value ? getFileNameFromUrl(value) : ""}
          </Typography>
        </Box>

        {/* Loading Indicator */}
        {loading && (
          <LinearProgress
            color="primary"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 2,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          />
        )}

        {/* Action Buttons */}
        <Box
          className="file-actions"
          sx={{
            display: "flex",
            gap: 1,
            opacity: 0,
            transition: "opacity 0.2s",
            ml: 1,
          }}
        >
          <Tooltip title="Preview">
            <span>
              <IconButton
                size="small"
                onClick={handlePreviewFile}
                disabled={loading}
              >
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download">
            <span>
              <IconButton
                size="small"
                onClick={handleDownloadFile}
                disabled={loading}
              >
                <Download fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={deleteDisabled || loading}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Stack>

      {imageDialog.view && (
        <ViewImageDialog
          open={imageDialog.view}
          file={imageDialog.data}
          orgImageUrl={value || ""}
          fileType={imageDialog.type}
          onClose={() =>
            setImageDialog({ view: false, data: "", type: "OTHER" })
          }
        />
      )}
    </>
  );
};
